import { useState } from "react";
import intl from "react-intl-universal";
import { Link, useLocation } from "react-router-dom";
import { ListItemIcon, ListItemText, Box, IconButton } from "@mui/material";
import { TbChevronLeft, TbEdit, TbLayoutGrid } from "react-icons/tb";
import { Container, MenuItem, Menu } from "../../../../Components/Molecules/MenuNavigation/styles";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useAuth } from "../../../../Contexts/AuthContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import {
  evaluateCapability,
  hasBaasBankingRails,
  isBaasCardsOnlyInstance,
  isBaasFullConsoleRole,
} from "../baasCapabilities";
import {
  BAAS_ACCOUNTS_PATH,
  BAAS_CARTOES_PATH,
  BAAS_HOMOLOGACAO_PATH,
  BAAS_INSTANCE_ADMIN_NAV_ITEMS,
  BAAS_NAV_ITEMS,
  BAAS_ONBOARD_PATH,
  BAAS_PLD_PATH,
  BAAS_ROOT_PATH,
  BAAS_STABLES_WALLET_PATH,
  BAAS_TRANSACTIONS_PATH,
  BAAS_VIRTUAL_ACCOUNTS_PATH,
} from "../constants";
import { needsBaasHomologacao } from "../Instances/baasHomologacao";
import { BaasInstanceEnvironmentBadge } from "../Instances/components/BaasInstanceEnvironmentBadge";
import { BaasKybStatusBadge } from "../Instances/components/BaasKybStatusBadge";
import { BaasEditInstanceModal } from "../Instances/components/BaasEditInstanceModal";

const isBaasItemActive = (pathname, item) => {
  if (item.exact) {
    return pathname === item.path;
  }

  return pathname === item.path || pathname.startsWith(`${item.path}/`);
};

const listItemTextSx = { m: 0 };

const sectionHeaderSx = {
  px: 1.5,
  pt: 1.5,
  pb: 0.5,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#9ca3af",
};

/** Hide product nav when the matching instance feature is disabled. */
const isNavItemVisible = (item, capabilities) => {
  // Unfinished products (Crossborder / Liquidex / Relatórios) — hide for all.
  if (item.navHidden) {
    return false;
  }

  if (item.path === BAAS_CARTOES_PATH) {
    const cardsEnabled = capabilities?.products?.cards?.enabled === true;
    const issuerEnabled = capabilities?.products?.issuer?.enabled === true;
    return cardsEnabled || issuerEnabled;
  }

  if (item.path === BAAS_STABLES_WALLET_PATH) {
    return capabilities?.products?.stables?.enabled === true;
  }

  if (item.path === "/baas/entidades") {
    // Cards-only (emissor) keeps Entidades for KYC / onboarding.
    return capabilities?.products?.entities?.enabled !== false;
  }

  // Contas = correntista banking client accounts only.
  // Root menu holds owner + shells (cards-only emissor uses Root for aporte/extrato).
  if (item.path === BAAS_ACCOUNTS_PATH) {
    if (capabilities?.products?.entities?.enabled === false) return false;
    return hasBaasBankingRails(capabilities);
  }

  if (item.path === BAAS_ROOT_PATH) {
    return hasBaasBankingRails(capabilities) || isBaasCardsOnlyInstance(capabilities);
  }

  if (
    item.path === BAAS_TRANSACTIONS_PATH ||
    item.path === BAAS_VIRTUAL_ACCOUNTS_PATH
  ) {
    return hasBaasBankingRails(capabilities);
  }

  if (item.path === BAAS_PLD_PATH) {
    return hasBaasBankingRails(capabilities)
      || capabilities?.products?.stables?.enabled === true;
  }

  return true;
};

export const BaasSidebar = ({ onNavigate }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { selectedInstance, instances, updateInstance, clearSelectedInstance } = useBaasInstance();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const canAccessCarteira = user.canAccessCarteira !== false;
  const { capabilities, isReadOnly, isMaster } = useBaasCapabilities();
  const { isValutConfigAdmin, canUseViewAs } = useBaasPlatformCreateAccess();
  const showHomologNav =
    needsBaasHomologacao(selectedInstance, instances) ||
    location.pathname === BAAS_HOMOLOGACAO_PATH ||
    location.pathname.startsWith(`${BAAS_HOMOLOGACAO_PATH}/`);

  const visibleNavItems = BAAS_NAV_ITEMS.filter((item) => {
    if (item.valutAdminOnly && !canUseViewAs) return false;
    return isNavItemVisible(item, capabilities);
  });

  const visibleAdminNavItems = BAAS_INSTANCE_ADMIN_NAV_ITEMS.filter((item) => {
    if (item.valutAdminOnly && !canUseViewAs) return false;
    if (item.requireAllowGlobalAccess) {
      if (!isValutConfigAdmin || selectedInstance?.allowGlobalAccess !== true) return false;
    }
    if (item.hideWhenReadOnly && !isMaster && isReadOnly) return false;
    if (item.requireCapability) {
      // Instance admin / master: full console on this card. Valut matrix admin keeps
      // Configurações even when the client membership lacks console.write.
      const isFullConsole = isMaster || isBaasFullConsoleRole(capabilities);
      if (
        !isFullConsole &&
        !(item.requireCapability === "settings.write" && isValutConfigAdmin) &&
        !evaluateCapability(capabilities, item.requireCapability).allowed
      ) {
        return false;
      }
    }
    if (item.showWhenHomologNeeded && !showHomologNav) return false;
    return true;
  });

  const handleEditInstance = async (payload) => {
    if (!selectedInstance) {
      return;
    }

    setUpdating(true);
    try {
      await updateInstance(selectedInstance.id, payload);
      setEditModalOpen(false);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
    <Container
      aria-label={intl.get("baas.nav.sidebarLabel")}
      style={{ height: "100%", minHeight: "100%" }}
    >
      {selectedInstance ? (
        <Box sx={{ px: 0.5, pb: 1, flexShrink: 0 }}>
          <Box
            sx={{
              mx: 0.5,
              px: 1.5,
              py: 1.25,
              borderRadius: 1.5,
              border: (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid #262626"
                  : "1px solid #e5e7eb",
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#fafafa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: "rgba(231, 167, 63, 0.15)",
                  color: "#a66f1d",
                  flexShrink: 0,
                }}
              >
                {selectedInstance.initials}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedInstance.name}
                </Box>
                <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
                  <BaasInstanceEnvironmentBadge environment={selectedInstance.environment} />
                  <BaasKybStatusBadge status={selectedInstance.kybStatus} />
                </Box>
              </Box>
              {!selectedInstance.baasInstanceId ? (
                <IconButton
                  size="small"
                  onClick={() => setEditModalOpen(true)}
                  aria-label={intl.get("baas.instances.edit", { name: selectedInstance.name })}
                  sx={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    color: "#9ca3af",
                    "&:hover": { color: "#e7a73f", bgcolor: "rgba(231, 167, 63, 0.08)" },
                  }}
                >
                  <TbEdit size={15} />
                </IconButton>
              ) : null}
            </Box>
          </Box>
        </Box>
      ) : null}

      <Menu>
        <Link
          to="/baas"
          className="link-no-zoom"
          onClick={() => {
            clearSelectedInstance();
            onNavigate?.();
          }}
        >
          <MenuItem $isSelected={false} selected={false} disableRipple>
            <ListItemIcon sx={{ minWidth: 28, mr: 0.5, color: "inherit" }}>
              <TbLayoutGrid size={18} />
            </ListItemIcon>
            <ListItemText
              sx={listItemTextSx}
              primary={intl.get("baas.instances.sidebar.allInstances")}
              primaryTypographyProps={{
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.25,
                noWrap: true,
              }}
            />
          </MenuItem>
        </Link>

        <Box sx={sectionHeaderSx}>
          {intl.get("baas.instances.sidebar.consoleSection")}
        </Box>

        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          const isSelected =
            item.path === "/baas/entidades"
              ? isBaasItemActive(location.pathname, item)
                || location.pathname === BAAS_ONBOARD_PATH
                || location.pathname.startsWith(`${BAAS_ONBOARD_PATH}/`)
              : isBaasItemActive(location.pathname, item);
          const label = intl.get(item.labelKey);

          return (
            <Link key={item.path} to={item.path} className="link-no-zoom" onClick={onNavigate}>
              <MenuItem $isSelected={isSelected} selected={false} disableRipple>
                <ListItemIcon sx={{ minWidth: 28, mr: 0.5, color: "inherit" }}>
                  <Icon size={18} />
                </ListItemIcon>
                <ListItemText
                  sx={listItemTextSx}
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: isSelected ? 600 : 500,
                    lineHeight: 1.25,
                    noWrap: true,
                  }}
                />
              </MenuItem>
            </Link>
          );
        })}

        {visibleAdminNavItems.length > 0 ? (
          <>
            <Box sx={sectionHeaderSx}>
              {intl.get("baas.admin.section")}
            </Box>

            {visibleAdminNavItems.map((item) => {
              const Icon = item.icon;
              const isSelected = isBaasItemActive(location.pathname, item);
              const label = intl.get(item.labelKey);

              return (
                <Link key={item.path} to={item.path} className="link-no-zoom" onClick={onNavigate}>
                  <MenuItem $isSelected={isSelected} selected={false} disableRipple>
                    <ListItemIcon sx={{ minWidth: 28, mr: 0.5, color: "inherit" }}>
                      <Icon size={18} />
                    </ListItemIcon>
                    <ListItemText
                      sx={listItemTextSx}
                      primary={label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isSelected ? 600 : 500,
                        lineHeight: 1.25,
                        noWrap: true,
                      }}
                    />
                  </MenuItem>
                </Link>
              );
            })}
          </>
        ) : null}
      </Menu>

      <Box sx={{ flex: 1, minHeight: 8 }} aria-hidden />

      {canAccessCarteira ? (
      <Box
        sx={{
          flexShrink: 0,
          pt: 1,
          borderTop: (theme) =>
            theme.palette.mode === "dark"
              ? "1px solid #262626"
              : "1px solid #e5e7eb",
        }}
      >
        <Link to="/" className="link-no-zoom" onClick={onNavigate}>
          <MenuItem $isSelected={false} selected={false} disableRipple>
            <ListItemIcon sx={{ minWidth: 28, mr: 0.5, color: "inherit" }}>
              <TbChevronLeft size={18} />
            </ListItemIcon>
            <ListItemText
              sx={listItemTextSx}
              primary={intl.get("baas.nav.backToApp")}
              primaryTypographyProps={{
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.25,
                noWrap: true,
              }}
            />
          </MenuItem>
        </Link>
      </Box>
      ) : null}
    </Container>

    <BaasEditInstanceModal
      open={editModalOpen}
      instance={selectedInstance}
      onClose={() => setEditModalOpen(false)}
      onSubmit={handleEditInstance}
      submitting={updating}
      canEditCatalogFlags={
        Boolean(selectedInstance?.baasInstanceId) && Boolean(isValutConfigAdmin)
      }
    />
    </>
  );
};
